export type ValuationMethod = "MULTIPLES" | "DCF" | "ASSET_BASED" | "RULE_OF_THUMB";

export const ALL_VALUATION_METHODS: ValuationMethod[] = [
  "MULTIPLES",
  "DCF",
  "ASSET_BASED",
  "RULE_OF_THUMB",
];

export interface MetricRequirement {
  metric: string;
  minimumYears: number;
  importance: "required" | "recommended";
  description: string;
  satisfiedByDocTypes: string[];
}

export interface MethodRequirement {
  method: ValuationMethod;
  label: string;
  minimumYears: number;
  requiredFields: FieldRequirement[];
  requiredMetrics: MetricRequirement[];
}

export interface FieldRequirement {
  field: string;
  description: string;
  importance: "required" | "recommended";
}

export const METHOD_REQUIREMENTS: Record<ValuationMethod, MethodRequirement> = {
  MULTIPLES: {
    method: "MULTIPLES",
    label: "Market Multiples",
    minimumYears: 1,
    requiredFields: [
      { field: "industry", description: "Industry classification", importance: "required" },
      { field: "revenue", description: "Annual revenue", importance: "required" },
      { field: "sde", description: "Seller's discretionary earnings (SDE)", importance: "required" },
    ],
    requiredMetrics: [
      {
        metric: "REVENUE",
        minimumYears: 1,
        importance: "required",
        description: "At least 1 year of revenue data",
        satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS", "SALES_REPORT"],
      },
      {
        metric: "SDE",
        minimumYears: 1,
        importance: "required",
        description: "SDE or owner compensation data",
        satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
      },
      {
        metric: "OWNER_COMPENSATION",
        minimumYears: 1,
        importance: "recommended",
        description: "Owner compensation breakdown",
        satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS", "W2_1099"],
      },
    ],
  },

  DCF: {
    method: "DCF",
    label: "Discounted Cash Flow",
    minimumYears: 2,
    requiredFields: [
      { field: "industry", description: "Industry classification", importance: "required" },
    ],
    requiredMetrics: [
      {
        metric: "REVENUE",
        minimumYears: 2,
        importance: "required",
        description: "At least 2 years of revenue data",
        satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
      },
      {
        metric: "EARNINGS_BEFORE_TAX",
        minimumYears: 2,
        importance: "required",
        description: "At least 2 years of earnings before tax",
        satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
      },
      {
        metric: "DEPRECIATION",
        minimumYears: 1,
        importance: "recommended",
        description: "Depreciation schedule",
        satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS", "BALANCE_SHEET"],
      },
      {
        metric: "INTEREST_EXPENSE",
        minimumYears: 1,
        importance: "recommended",
        description: "Interest expense data",
        satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS", "DEBT_SCHEDULE"],
      },
      {
        metric: "INCOME_TAX",
        minimumYears: 1,
        importance: "recommended",
        description: "Income tax paid",
        satisfiedByDocTypes: ["TAX_RETURN"],
      },
      {
        metric: "CURRENT_ASSETS",
        minimumYears: 1,
        importance: "recommended",
        description: "Current assets for working capital calculation",
        satisfiedByDocTypes: ["BALANCE_SHEET", "TAX_RETURN"],
      },
      {
        metric: "CURRENT_LIABILITIES",
        minimumYears: 1,
        importance: "recommended",
        description: "Current liabilities for working capital calculation",
        satisfiedByDocTypes: ["BALANCE_SHEET", "TAX_RETURN"],
      },
    ],
  },

  ASSET_BASED: {
    method: "ASSET_BASED",
    label: "Asset-Based",
    minimumYears: 1,
    requiredFields: [],
    requiredMetrics: [
      {
        metric: "TOTAL_ASSETS",
        minimumYears: 1,
        importance: "required",
        description: "Total assets or PPE value",
        satisfiedByDocTypes: ["BALANCE_SHEET", "TAX_RETURN"],
      },
      {
        metric: "TOTAL_LIABILITIES",
        minimumYears: 1,
        importance: "recommended",
        description: "Total liabilities",
        satisfiedByDocTypes: ["BALANCE_SHEET", "TAX_RETURN"],
      },
      {
        metric: "INVENTORY",
        minimumYears: 1,
        importance: "recommended",
        description: "Inventory valuation",
        satisfiedByDocTypes: ["BALANCE_SHEET", "INVENTORY_REPORT"],
      },
      {
        metric: "PPE_NET",
        minimumYears: 1,
        importance: "recommended",
        description: "Net property, plant & equipment",
        satisfiedByDocTypes: ["BALANCE_SHEET", "TAX_RETURN"],
      },
    ],
  },

  RULE_OF_THUMB: {
    method: "RULE_OF_THUMB",
    label: "Rule of Thumb",
    minimumYears: 1,
    requiredFields: [
      { field: "industry", description: "Industry classification", importance: "required" },
    ],
    requiredMetrics: [
      {
        metric: "REVENUE",
        minimumYears: 1,
        importance: "required",
        description: "At least 1 year of revenue",
        satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS", "SALES_REPORT"],
      },
      {
        metric: "SDE",
        minimumYears: 1,
        importance: "recommended",
        description: "SDE for additional rule-of-thumb benchmarks",
        satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
      },
    ],
  },
};
